import dynamic from 'next/dynamic';
import { SpinnerIcon } from 'shared/icons/SpinnerIcon';
import type { MapPOI } from 'shared/map/interfaces';
import { addDays, formatClientDate } from 'shared/util/datetime-fns';
import { thousandSeparated } from 'shared/util/formatting';
import { verticals } from '../../data/verticals';
import { Offer } from './Offer';

const ClientSideMap = dynamic(() => import('shared/map/ClientSideMap'), { ssr: false });

const defaultImage = 'https://travel-images.finncdn.no/dynamic/480x360c/seo/default.jpg';
const formattedDate = (plusDays: number): string => formatClientDate(addDays(plusDays, Date.now()));

interface OfferItem {
    heading?: string;
    name?: string;
    longitude: number;
    latitude: number;
}

interface Props {
    loading?: boolean;
    destination: string;
    placeKey?: string;
    offers: OfferItem[];
    vertical: string;
    heading: string;
    showMap: boolean;
}

export const Offers = ({ destination, placeKey, offers, vertical, heading, showMap, loading = false }: Props) => {
    const mapPOIs: MapPOI[] = offers.map((offer: OfferItem) => ({
        name: offer.heading ?? offer.name,
        coordinates: [offer.longitude, offer.latitude],
    }));

    const renderOffer = (offer) => {
        switch (vertical) {
            case verticals.hotel: {
                const hotelKey = offer.key.split(':')[1];
                const path = `p${placeKey}-h${hotelKey}-details/${formattedDate(1)}/${formattedDate(2)}/2adults`;
                return (
                    <Offer
                        id={hotelKey}
                        heading={offer.name}
                        location={offer.address}
                        rating={offer.starRating}
                        image={offer.images[0]?.small ?? defaultImage}
                        url={`https://www.finn.no/reise/hotell/wl/#/hotels/${path}`}
                        key={hotelKey}
                    />
                );
            }
            case verticals.prepackage:
                return (
                    <Offer
                        id={offer.hotelId}
                        heading={offer.hotelName}
                        location="Pakkereise"
                        price={`${offer.duration} dager fra ${offer.pricePerPerson} kr`}
                        rating={offer.rating}
                        image={`https://travel-images.finncdn.no/dynamic/480x360c${offer.imagePaths[0]}`}
                        url={`https://www.finn.no/reise/pakkereiser/hotell?hotellId=${offer.hotelId}`}
                        key={offer.hotelId}
                    />
                );
            case verticals.fhh: {
                const imageBaseUrl = offer.ad_type === 'private' ? 'https://dev-images.finncdn.no' : 'https://travel-images.finncdn.no';
                return (
                    <Offer
                        id={offer.id}
                        heading={offer.heading}
                        location={offer.locality}
                        price={`Fra ${thousandSeparated(offer.price_min)} kr per natt`}
                        image={offer.imageurl ? `${imageBaseUrl}/dynamic/480x360c/${offer.imageurl}` : defaultImage}
                        url={`https://www.finn.no/reise/feriehus-hytteutleie/ad.html?finnkode=${offer.id}`}
                        key={offer.id}
                    />
                );
            }
        }
    };

    if (loading) {
        return (
            <section className="mt-32 mb-64 mx-16" aria-labelledby="seo-recommended-title">
                <h2 id="seo-recommended-title" className="text-center pb-16">
                    {heading} {destination}
                </h2>
                <SpinnerIcon />
            </section>
        );
    }

    return offers.length ? (
        <section className="mt-32 mb-64 mx-16" aria-labelledby="seo-recommended-title">
            {showMap && (
                <ClientSideMap
                    id="seo-map"
                    primaryPois={mapPOIs}
                    className="seo-map-container mb-32"
                    hasClustering={vertical === verticals.hotel}
                    disableScrollZoomUnlessFocused
                />
            )}
            <h2 id="seo-recommended-title" className="text-center pb-16">
                {heading} {destination}
            </h2>
            <div className="md:grid md:grid-cols-2 gap-8">
                {offers.slice(0, offers.length < 6 ? 2 * Math.floor(offers.length / 2) : 6).map(renderOffer)}
            </div>
        </section>
    ) : null;
};
